import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef } from 'react';
import { uniqueId as _uniqueId } from 'lodash-es';
import { TooltipGroup } from '@/components/shared/tooltip/TooltipGroup';
import { useTableConfigPersistence } from '@/hooks/useTableConfigPersistence';
import { DownloadButtonProps } from '@/types/download/DownloadButtonProps';
import { CustomColumnDef, TableOptions, TableState } from '@/types/table';
import styles from './Table.module.scss';
import TableInternal from './components/TableInternal';
import { useColumnConfig } from './hooks/useColumnConfig';
import useTableStore from './store/useTableStore';
import { getAdditionalHeaderLabels, getPageSize, normalizeMergedGroups, useValidateTableProps } from './utils/utils';
import { applyColumnConfig, buildDefaultColumnConfig } from './utils/columnConfig';
import {
  applyColumnVisibilityToDownloadQuery,
  applyTableSortToDownloadQuery,
  buildColumnFieldMapFromColumns,
} from './utils/downloadColumns';

type InitialTableRegistrationState = Parameters<ReturnType<typeof useTableStore.getState>['register']>[1];

const buildInitialTableRegistrationState = <T,>(
  options?: TableOptions<T>,
  initState?: TableState,
): InitialTableRegistrationState => ({
  pagination: {
    pageIndex: 0,
    pageSize: getPageSize({ options: options ?? {} }),
  },
  sorting: initState?.sort ?? options?.defaultSorting,
});

const buildAdjustTableColumns = <T,>(
  columns: CustomColumnDef<T>[],
  draftConfig: ReturnType<typeof buildDefaultColumnConfig>,
): CustomColumnDef<T>[] => {
  return applyColumnConfig(columns, draftConfig).map(column => ({
    ...column,
    // Keep user-hidden columns reorderable in the Order tab while still
    // preserving the draft pinning and column order.
    visible: true,
  }));
};

export type TableProps<T> = {
  'columns': CustomColumnDef<T>[];
  'data': T[];
  'isLoading'?: boolean;
  'filter'?: string;
  'searchValue'?: string;
  'onSearchChange'?: (value: string) => void;
  'onSearchClear'?: () => void;
  'searchPlaceholder'?: string;
  'initState'?: TableState;
  'className'?: string;
  'options'?: TableOptions<T>;
  'id'?: string;
  'index'?: string;
  'downloadButtonProps'?: DownloadButtonProps;
  'downloadButtonPlacement'?: 'toolbar' | 'external';
  'onDownloadButtonPropsChange'?: (
    props: DownloadButtonProps | undefined,
    sourceProps: DownloadButtonProps | undefined,
  ) => void;
  'toolbarElement'?: ReactNode;
  'headerElement'?: ReactNode;
  'filterSlot'?: ReactNode;
  'filterSlotWidth'?: CSSProperties['width'];
  'preTableSlot'?: ReactNode;
  'data-testid'?: string;
  'tooltipDelayShow'?: number;
  'showToolbarBorder'?: boolean;
  'enableColumnResizing'?: boolean;
  'enableColumnConfig'?: boolean;
  'username'?: string;
  'externalInteractionSignal'?: unknown;
  'additionalActionBarElement'?: ReactNode;
};

export const Table = <T,>({
  id,
  options,
  data,
  initState,
  tooltipDelayShow,
  enableColumnResizing = true,
  enableColumnConfig = false,
  downloadButtonPlacement = 'toolbar',
  onDownloadButtonPropsChange,
  username,
  ...props
}: TableProps<T>) => {
  const tableState = useTableStore(state => state.state);
  const register = useTableStore(state => state.register);
  const unregister = useTableStore(state => state.unregister);
  const initialRegistrationStateById = useRef<Record<string, InitialTableRegistrationState>>({});

  const tableId = useMemo(() => id ?? _uniqueId('table'), [id]);

  if (!initialRegistrationStateById.current[tableId]) {
    initialRegistrationStateById.current[tableId] = buildInitialTableRegistrationState(options, initState);
  }

  useValidateTableProps(tableId, props.columns, options);

  const { columns } = props;
  const { additionalHeaderRows } = options || {};

  const additionalHeaderLabels = useMemo(() => getAdditionalHeaderLabels(additionalHeaderRows), [additionalHeaderRows]);

  const groupedColumns = useMemo(() => normalizeMergedGroups(columns), [columns]);

  // Columns hidden by mode (visible === false) must not surface in the Adjust
  // table dialog. The Order tab already filters them; mirror that here so the
  // Display/Pinning tabs don't list mode-hidden duplicates (e.g. two "HCO Name"
  // columns) or leak hidden sticky columns into the pinned section.
  const configColumns = useMemo(
    () => groupedColumns.filter(column => column.visible === undefined || column.visible),
    [groupedColumns],
  );

  const defaultColumnConfig = useMemo(
    () => buildDefaultColumnConfig(configColumns, additionalHeaderLabels),
    [additionalHeaderLabels, configColumns],
  );
  const columnIds = useMemo(() => Object.keys(defaultColumnConfig.visibility), [defaultColumnConfig]);

  const {
    config: persistedColumnConfig,
    isLoading: isColumnConfigLoading,
    saveConfig,
    resetConfig,
  } = useTableConfigPersistence({
    username: enableColumnConfig ? username : undefined,
    tableId,
    columnIds,
    defaultConfig: defaultColumnConfig,
  });

  const columnConfig = useColumnConfig({
    columns: configColumns,
    additionalHeaderLabels,
    persistedConfig: enableColumnConfig ? (persistedColumnConfig ?? defaultColumnConfig) : null,
    onSave: saveConfig,
    onReset: resetConfig,
  });

  const configuredColumns = useMemo(
    () => (enableColumnConfig ? applyColumnConfig(props.columns, columnConfig.appliedConfig) : props.columns),
    [columnConfig.appliedConfig, enableColumnConfig, props.columns],
  );

  const downloadButtonProps = useMemo(() => {
    const original = props.downloadButtonProps;
    if (!original || !enableColumnConfig) {
      return original;
    }

    const columnFieldMap = original.columnFieldMap ?? buildColumnFieldMapFromColumns(props.columns);

    const filteredBaseQuery = applyColumnVisibilityToDownloadQuery(
      original.baseQuery,
      columnFieldMap,
      columnConfig.appliedConfig.visibility,
    );

    const sortedBaseQuery = applyTableSortToDownloadQuery(
      filteredBaseQuery,
      columnFieldMap,
      tableState[tableId]?.sorting ?? [],
    );

    return {
      ...original,
      baseQuery: sortedBaseQuery,
    };
  }, [
    props.downloadButtonProps,
    props.columns,
    enableColumnConfig,
    columnConfig.appliedConfig.visibility,
    tableState,
    tableId,
  ]);

  useEffect(() => {
    onDownloadButtonPropsChange?.(downloadButtonProps, props.downloadButtonProps);
  }, [downloadButtonProps, onDownloadButtonPropsChange, props.downloadButtonProps]);

  const resolvedFilter = useMemo(() => {
    if (options?.lazy) {
      return props.filter;
    }

    return props.searchValue ?? props.filter;
  }, [options?.lazy, props.filter, props.searchValue]);

  const adjustTableColumns = useMemo(
    () => (enableColumnConfig ? buildAdjustTableColumns(configColumns, columnConfig.draftConfig) : configuredColumns),
    [columnConfig.draftConfig, configColumns, configuredColumns, enableColumnConfig],
  );

  useEffect(() => {
    const initialRegistrationStates = initialRegistrationStateById.current;

    register(tableId, initialRegistrationStates[tableId]);

    return () => {
      delete initialRegistrationStates[tableId];
      unregister(tableId);
    };
  }, [register, tableId, unregister]);

  if (!tableState[tableId]) {
    return null;
  }

  return (
    <TooltipGroup id={tableId} tooltipClassName={styles.customTooltip} delayShow={tooltipDelayShow}>
      <TableInternal
        {...props}
        downloadButtonProps={downloadButtonPlacement === 'toolbar' ? downloadButtonProps : undefined}
        columns={configuredColumns}
        adjustTableColumns={adjustTableColumns}
        filter={resolvedFilter}
        id={tableId}
        options={options}
        data={data}
        enableColumnResizing={enableColumnResizing}
        enableColumnConfig={enableColumnConfig}
        columnConfig={enableColumnConfig ? columnConfig : undefined}
        isColumnConfigLoading={enableColumnConfig ? isColumnConfigLoading : false}
      />
    </TooltipGroup>
  );
};

export default Table;
